"use client";

import React from "react";
import { useEffect, useState } from "react";
import { v3, inkAccent } from "@/lib/theme";
import { TOTALS, PORTFOLIO, VALUE_SERIES, fmtEur } from "@/lib/data/mock";
import { Button } from "@/components/ui/button";
import { Sparkline } from "@/components/ui/charts";
import { V3SectionHead } from "./V3SectionHead";
import { motion } from "framer-motion";

export function V3SectionBank() {
  const [step, setStep] = React.useState(0);
  const [running, setRunning] = React.useState(true);

  React.useEffect(() => {
    if (!running) return;
    if (step >= 3) {
      const id = setTimeout(() => setStep(0), 2400);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setStep(step + 1), 1400);
    return () => clearTimeout(id);
  }, [step, running]);

  const ink = inkAccent();
  const steps = [
    { n: "01", title: "Daten sammeln", sub: "Portfolio, Mietverträge, Kredit" },
    { n: "02", title: "Berechnen",     sub: "DSCR, LTV, Cashflow 60M" },
    { n: "03", title: "Exportieren",   sub: "PDF · XLS · 7 Seiten" },
  ];

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px" }} className="rp-section"
    >
      <V3SectionHead n={6} kicker="Bereit für die Bank"
        title="Ein Klick."
        dimWord="Deine Bankenpräsentation."
        sub="Du planst den nächsten Ankauf. Die Bank will Zahlen — realpilot exportiert sie. Mit deinem Namen drauf. Sonst nichts." />

      <div className="rp-stack" style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 40, alignItems: "start" }}>
        {/* Animated export flow */}
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, padding: 32 }}>
          <div style={{ font: `12px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 24 }}>Export-Flow · Live</div>
          {steps.map((s, i) => {
            const active = step >= i;
            const current = step === i;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 18, padding: "18px 0", borderTop: i > 0 ? `1px solid ${v3.line}` : 0 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 99,
                  border: `1.5px solid ${active ? v3.accent : v3.line2}`,
                  background: active ? v3.accent : "transparent",
                  color: active ? v3.bg : v3.dim,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  font: `500 13px ${v3.mono}`, transition: "all .4s",
                }}>
                  {active ? (
                    <svg viewBox="0 0 14 14" width="14" height="14"><path d="M3 7 L6 10 L11 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  ) : s.n}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ font: `16px ${v3.sans}`, color: active ? v3.fg : v3.dim, fontWeight: 500 }}>{s.title}</div>
                  <div style={{ font: `12px ${v3.sans}`, color: v3.dim2, marginTop: 2 }}>{s.sub}</div>
                </div>
                {current && (
                  <div style={{ width: 80, height: 4, background: v3.line2, borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ width: "100%", height: 4, background: v3.accent, animation: "v3-stepfill 1.4s linear" }} />
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${v3.line}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button className="flex-1 min-w-[150px]">
              PDF herunterladen
            </Button>
            <Button variant="outline">
              XLS
            </Button>
          </div>
          <div style={{ marginTop: 12, font: `11px ${v3.mono}`, color: v3.dim, textAlign: "center", letterSpacing: "0.08em" }}>
            ZEIT BIS FERTIG · 42 SEKUNDEN
          </div>
        </div>

        {/* Document preview */}
        <div style={{ background: v3.bg, border: `1px solid ${v3.line2}`, borderRadius: 16, overflowX: "auto", boxShadow: v3.intense ? `0 0 0 1px ${v3.accentBg}, 0 24px 80px -40px ${v3.accentDim}` : "none" }}>
          <div style={{ minWidth: 600 }}>
            <div style={{ padding: "14px 24px", borderBottom: `1px solid ${v3.line}`, display: "flex", justifyContent: "space-between", font: `12px ${v3.mono}`, color: v3.dim }}>
              <span style={{ color: v3.accent }}>portfolio_anna_g_2026q2.pdf</span>
              <span>Seite 1 / 7</span>
            </div>
            <div style={{ padding: 40 }}>
              <div style={{ font: `11px ${v3.mono}`, color: v3.dim, letterSpacing: "0.06em", textTransform: "uppercase" }}>Vertraulich · Kreditinstitut</div>
              <div style={{ font: `500 38px ${v3.sans}`, color: v3.fg, marginTop: 8, letterSpacing: "-0.03em" }}>Portfolioübersicht</div>
              <div style={{ font: `14px ${v3.sans}`, color: v3.dim, marginTop: 4 }}>Anna G. · Stichtag 31.03.2026 · 6 Objekte</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", marginTop: 28, gap: 12 }}>
                {[["Marktwert", fmtEur(TOTALS.wert)], ["Restschuld", "€ 1.247.000"], ["LTV (Ø)", "64,2 %"], ["DSCR (Ø)", "1,38"]].map((k, i) => (
                  <div key={i} style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 10, padding: "14px 16px" }}>
                    <div style={{ font: `11px ${v3.sans}`, color: v3.dim }}>{k[0]}</div>
                    <div style={{ font: `500 22px ${v3.sans}`, color: v3.fg, marginTop: 6, letterSpacing: "-0.02em" }}>{k[1]}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 28, font: `11px ${v3.mono}`, color: v3.dim, letterSpacing: "0.06em", textTransform: "uppercase" }}>Objekte</div>
              <div style={{ marginTop: 12, border: `1px solid ${v3.line}`, borderRadius: 10, overflow: "hidden" }}>
                {PORTFOLIO.map((o: any, i: number) => (
                  <div key={o.id} style={{ display: "grid", gridTemplateColumns: "30px 1.2fr 1fr 80px 80px 60px", padding: "12px 14px", borderTop: i > 0 ? `1px solid ${v3.line}` : 0, font: `12.5px ${v3.mono}`, color: v3.fg2, alignItems: "center" }}>
                    <span style={{ color: v3.dim2 }}>{String(o.id).padStart(2, "0")}</span>
                    <span style={{ font: `13px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>{o.city}</span>
                    <span style={{ color: v3.dim }}>{o.typ}</span>
                    <span style={{ textAlign: "right" }}>{fmtEur(o.mwert / 1000)}K</span>
                    <span style={{ textAlign: "right" }}>{fmtEur(o.miete)}</span>
                    <span style={{ textAlign: "right" }}>{o.rendite.toFixed(1).replace(".", ",")}%</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 28, font: `11px ${v3.mono}`, color: v3.dim, letterSpacing: "0.06em", textTransform: "uppercase" }}>Wertentwicklung · 24M</div>
              <div style={{ marginTop: 14, color: ink }}>
                <Sparkline data={VALUE_SERIES} height={90} width={620} stroke={ink} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.05)"} strokeWidth={1.4} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
